'use client'

import { useTranslation } from '@shared/config/i18n'
import { FEATURE_FLAG_KEYS } from '@shared/constants'

import { useFlagsCompat } from '@podo/flags/react'

type ValidateSubscribeErrorCode =
  | 'DUPLICATE_LESSON'
  | 'DUPLICATE_TRIAL_LESSON'
  | 'DELINQUENT_PAYMENT'
  | 'NOT_AUTHORIZED'
  | 'MUST_REGISTER_PHONE_NUMBER'
  | 'HAS_TICKET_HISTORY'
  | 'HAS_ACTIVE_TICKET'

export const DEFAULT_SUBSCRIBE_ERROR_MAP: Record<ValidateSubscribeErrorCode, string> = {
  DUPLICATE_LESSON: 'subscribeList.error.duplicateLesson',
  DUPLICATE_TRIAL_LESSON: 'subscribeList.error.duplicateTrialLesson',
  DELINQUENT_PAYMENT: 'subscribeList.error.delinquentPayment',
  NOT_AUTHORIZED: 'subscribeList.error.notAuthorized',
  MUST_REGISTER_PHONE_NUMBER: 'subscribeList.error.mustRegisterPhoneNumber',
  HAS_TICKET_HISTORY: 'subscribeList.error.hasTicketHistory',
  HAS_ACTIVE_TICKET: 'subscribeList.error.hasActiveTicket',
}

const isSubscribeErrorCode = (key: string): key is ValidateSubscribeErrorCode => key in DEFAULT_SUBSCRIBE_ERROR_MAP

const getDefaultSubscribeErrorMap = (t: (key: string) => string): Record<ValidateSubscribeErrorCode, string> => ({
  DUPLICATE_LESSON: t(DEFAULT_SUBSCRIBE_ERROR_MAP.DUPLICATE_LESSON),
  DUPLICATE_TRIAL_LESSON: t(DEFAULT_SUBSCRIBE_ERROR_MAP.DUPLICATE_TRIAL_LESSON),
  DELINQUENT_PAYMENT: t(DEFAULT_SUBSCRIBE_ERROR_MAP.DELINQUENT_PAYMENT),
  NOT_AUTHORIZED: t(DEFAULT_SUBSCRIBE_ERROR_MAP.NOT_AUTHORIZED),
  MUST_REGISTER_PHONE_NUMBER: t(DEFAULT_SUBSCRIBE_ERROR_MAP.MUST_REGISTER_PHONE_NUMBER),
  HAS_TICKET_HISTORY: t(DEFAULT_SUBSCRIBE_ERROR_MAP.HAS_TICKET_HISTORY),
  HAS_ACTIVE_TICKET: t(DEFAULT_SUBSCRIBE_ERROR_MAP.HAS_ACTIVE_TICKET),
})

export const parseSubscribeErrorMap = (
  value: unknown,
  defaultMap: Record<ValidateSubscribeErrorCode, string> = DEFAULT_SUBSCRIBE_ERROR_MAP,
): Record<ValidateSubscribeErrorCode, string> => {
  if (typeof value !== 'string') {
    return { ...defaultMap }
  }

  try {
    const parsed: unknown = JSON.parse(value)

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ...defaultMap }
    }

    return Object.entries(parsed).reduce<Record<ValidateSubscribeErrorCode, string>>(
      (acc, [key, message]) => {
        if (isSubscribeErrorCode(key) && typeof message === 'string' && message.trim()) {
          acc[key] = message
        }

        return acc
      },
      { ...defaultMap },
    )
  } catch {
    return { ...defaultMap }
  }
}

export const useSubscribeErrorStaticModel = () => {
  const t = useTranslation()
  const { validate_subscribe_error_message: validateSubscribeErrorMessage } = useFlagsCompat([
    FEATURE_FLAG_KEYS.VALIDATE_SUBSCRIBE_ERROR_MESSAGE,
  ])

  const SUBSCRIBE_ERROR_MAP = parseSubscribeErrorMap(
    validateSubscribeErrorMessage?.value,
    getDefaultSubscribeErrorMap(t),
  )

  return { SUBSCRIBE_ERROR_MAP }
}
